/**
 * Last-known-good cache for GET responses.
 *
 * WHY THIS EXISTS
 * ---------------
 * `offlineSnapshot` only covers what a *sale* needs (items, customers, prices,
 * receipt accounts). Every other screen - invoices, stock ledger, batches,
 * reports, customer statements - is a plain GET that simply died with the link,
 * so an outage emptied the whole app even though the browser still had the
 * session and the catalogue.
 *
 * So every successful GET is written here, keyed by method + URL + params +
 * the signed-in user, and served back when the network is gone. This is a
 * *read* cache only: it never answers a write, never merges, never invents
 * records. What the operator sees offline is exactly what the server returned
 * the last time it answered that exact query - and the shell tells them how old
 * that is rather than pretending it is live.
 *
 * GROWTH IS BOUNDED: entries are capped by size, by count and by age,
 * `pruneReadCache()` runs on mount, and every `PRUNE_CHECK_EVERY_WRITES` saves
 * re-check in an idle slot - a till that stays open for a week would otherwise
 * never come back through a mount to notice the cache had filled up. The POS
 * itself (outbox, printed documents) is untouched by pruning.
 */
import {
  OFFLINE_CACHE_INDEXES,
  OFFLINE_STORES,
  offlineClear,
  offlineGet,
  offlinePut,
  requestToPromise,
  withOfflineStore
} from './offlineDb';
import { whenIdle } from './idleScheduler';

/** A cached read older than this is refused outright (a fortnight-old price is a lie). */
export const READ_CACHE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** Past this point the UI must say "as of <time>" instead of showing it as live. */
export const READ_CACHE_STALE_MS = 2 * 60 * 1000;

/** Hard cap on stored responses, so the store cannot grow without bound. */
export const READ_CACHE_MAX_ENTRIES = 200;

/** A single response larger than this is not worth the quota (reports, dumps). */
export const READ_CACHE_MAX_ENTRY_BYTES = 512 * 1024;

/**
 * How many writes between opportunistic prune checks.
 *
 * Cheap enough to be invisible (the check walks key indexes, never payloads) and
 * frequent enough that a till left open all week cannot drift far past the cap.
 */
export const PRUNE_CHECK_EVERY_WRITES = 25;

let writesSincePruneCheck = 0;
let pruneCheckScheduled = false;

/** Re-check the cache size in an idle slot (never on the request path). */
const schedulePruneCheck = () => {
  if (pruneCheckScheduled) return;
  pruneCheckScheduled = true;
  whenIdle(async () => {
    pruneCheckScheduled = false;
    await pruneReadCache();
  }, { timeoutMs: 5000, fallbackDelayMs: 1500 });
};

const asString = (value) => (value == null ? '' : String(value));

/**
 * Deterministic param serialisation for the cache key.
 *
 * `?a=1&b=2` and `?b=2&a=1` must produce the same entry, otherwise the same
 * list would be stored twice and a later miss would look like "no data
 * offline" when the bytes are right there.
 */
export const serializeReadCacheParams = (params) => {
  if (!params) return '';
  const entries = params instanceof URLSearchParams
    ? Array.from(params.entries())
    : Object.entries(params);

  return entries
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, Array.isArray(value) ? value.map(asString).join(',') : asString(value)])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
};

/** Build the storage key for one read. `scope` separates tenants/users. */
export const buildReadCacheKey = ({ method = 'get', baseURL = '', url = '', params = null, scope = 'anon' } = {}) => {
  const query = serializeReadCacheParams(params);
  return [
    asString(scope) || 'anon',
    String(method).toLowerCase(),
    `${asString(baseURL)}${asString(url)}`
  ].join('|') + (query ? `?${query}` : '');
};

/** Cheap size probe. Unserialisable payloads are refused rather than guessed at. */
const estimatePayloadBytes = (payload) => {
  try {
    const json = JSON.stringify(payload);
    return json ? json.length : 0;
  } catch (_) {
    return Number.POSITIVE_INFINITY;
  }
};

/**
 * Store (or replace) one response.
 *
 * Resolves with `null` when the payload is too large, empty or unserialisable -
 * callers treat that as "not cached" and simply carry on.
 */
export const saveCachedResponse = async (key, payload, { now = Date.now() } = {}) => {
  if (!key || payload === undefined || payload === null) return null;
  const size = estimatePayloadBytes(payload);
  if (!Number.isFinite(size) || size > READ_CACHE_MAX_ENTRY_BYTES) return null;

  const record = {
    key: String(key),
    savedAt: new Date(now).toISOString(),
    savedAtMs: now,
    bytes: size,
    payload
  };

  try {
    await offlinePut(OFFLINE_STORES.CACHE, record);
    writesSincePruneCheck += 1;
    if (writesSincePruneCheck >= PRUNE_CHECK_EVERY_WRITES) {
      writesSincePruneCheck = 0;
      schedulePruneCheck();
    }
    return record;
  } catch (error) {
    // Quota exceeded, private mode, storage locked: caching is best-effort and
    // must never break the request that already succeeded.
    console.warn('[offline] could not cache read', error?.message || error);
    return null;
  }
};

/**
 * Read one response back.
 *
 * @returns {Promise<null | {payload: any, savedAt: string, ageMs: number, stale: boolean, bytes: number}>}
 */
export const readCachedResponse = async (key, { now = Date.now(), maxAgeMs = READ_CACHE_MAX_AGE_MS } = {}) => {
  if (!key) return null;
  try {
    const record = await offlineGet(OFFLINE_STORES.CACHE, String(key));
    if (!record) return null;

    const savedAtMs = Number(record.savedAtMs) || Date.parse(record.savedAt || '');
    if (!Number.isFinite(savedAtMs)) return null;

    const ageMs = Math.max(0, now - savedAtMs);
    if (ageMs > maxAgeMs) return null;

    return {
      payload: record.payload,
      savedAt: record.savedAt || new Date(savedAtMs).toISOString(),
      ageMs,
      stale: ageMs > READ_CACHE_STALE_MS,
      bytes: Number(record.bytes) || 0
    };
  } catch (error) {
    console.warn('[offline] could not read cached response', error?.message || error);
    return null;
  }
};

/**
 * Walk the age index without reading payloads.
 *
 * `IDBIndex.getAllKeys()` returns primary keys ordered by the index, but not the
 * index values themselves, so age cannot be judged from it. A key cursor yields
 * both (`key` = savedAtMs, `primaryKey` = cache key) and never deserialises a
 * stored response, which keeps pruning cheap on a full cache.
 */
const collectCacheIndexEntries = (store) => new Promise((resolve, reject) => {
  const entries = [];
  let request;
  try {
    request = store.index(OFFLINE_CACHE_INDEXES.BY_SAVED_AT).openKeyCursor();
  } catch (error) {
    reject(error);
    return;
  }
  request.onerror = () => reject(request.error || new Error('Could not read cache index.'));
  request.onsuccess = () => {
    const cursor = request.result;
    if (!cursor) {
      resolve(entries);
      return;
    }
    entries.push({ key: cursor.primaryKey, savedAtMs: Number(cursor.key) || 0 });
    cursor.continue();
  };
});

/**
 * Drop entries older than `maxAgeMs`, then the oldest ones until at most
 * `maxEntries` remain.
 */
export const pruneReadCache = async ({
  maxEntries = READ_CACHE_MAX_ENTRIES,
  maxAgeMs = READ_CACHE_MAX_AGE_MS,
  now = Date.now()
} = {}) => {
  let entries = [];
  try {
    entries = await withOfflineStore(OFFLINE_STORES.CACHE, 'readonly', collectCacheIndexEntries) || [];
  } catch (error) {
    console.warn('[offline] could not inspect read cache', error?.message || error);
    return { removed: 0 };
  }

  if (!entries.length) return { removed: 0 };

  // Entries arrive in index order, i.e. oldest first.
  const expired = entries.filter((entry) => (now - entry.savedAtMs) > maxAgeMs);
  const survivors = entries.filter((entry) => (now - entry.savedAtMs) <= maxAgeMs);
  const overflow = Math.max(0, survivors.length - Math.max(0, maxEntries));
  const toRemove = [...expired, ...survivors.slice(0, overflow)]
    .map((entry) => entry.key)
    .filter((key) => key !== undefined && key !== null);

  if (!toRemove.length) return { removed: 0 };

  try {
    await withOfflineStore(OFFLINE_STORES.CACHE, 'readwrite', (store) => {
      toRemove.forEach((key) => store.delete(key));
    });
  } catch (error) {
    console.warn('[offline] could not prune read cache', error?.message || error);
    return { removed: 0 };
  }

  return { removed: toRemove.length };
};

/** Wipe the cache (last user signed out, or the tenant changed). */
export const clearReadCache = () => offlineClear(OFFLINE_STORES.CACHE);

/** How many responses are stored (diagnostics / tests). */
export const countCachedResponses = async () => {
  const total = await withOfflineStore(OFFLINE_STORES.CACHE, 'readonly', (store) => (
    requestToPromise(store.count())
  ));
  return Number(total) || 0;
};
